import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { Alert } from "antd";
import { FaCheck } from "react-icons/fa";
import { CheckCircleFilled, WarningFilled } from "@ant-design/icons";
import moment from "moment";
import apiClient from "../../../../../helpers/apiClient";
import { getAxleNameCheck } from "./axleNameMatch";

// Axle policy dates are midnight UTC; format in UTC so they don't shift a day in US timezones
const formatPolicyDate = (value) => (value ? moment.utc(value).format("MMM D, YYYY") : "");

const formatMoney = (value) => `$${Number(value).toLocaleString()}`;

const formatCoverageText = (coverage, t) => {
    const details = [];
    if (coverage.limitPerPerson) {
        details.push(`${formatMoney(coverage.limitPerPerson)} ${t("resDetails.axlePerPerson")}`);
    }
    if (coverage.limitPerAccident) {
        details.push(`${formatMoney(coverage.limitPerAccident)} ${t("resDetails.axlePerAccident")}`);
    }
    if (coverage.deductible) {
        details.push(`${formatMoney(coverage.deductible)} ${t("resDetails.axleDeductible")}`);
    }

    const label = coverage.label || coverage.code || t("resDetails.axleCoverage");
    return `${label}: ${details.length > 0 ? details.join(" · ") : t("resDetails.axleCovered")}`;
};

// Axle reports `result: "manual"` with a `document-*` resultDetail when the customer uploaded
// an ID card instead of linking their insurer account
const isDocumentVerified = (policy) =>
    String(policy?.resultDetail || "").startsWith("document") || policy?.result === "manual";

const DetailRow = ({ label, children }) => (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-2">
        <span className="shrink-0 text-xs text-[#999] sm:w-32">{label}</span>
        <span className="min-w-0 break-words text-xs text-[#374151]">{children}</span>
    </div>
);

DetailRow.propTypes = {
    label: PropTypes.string.isRequired,
    children: PropTypes.node,
};

// Policyholder vs renter / additional driver, side by side so admins can compare either way
const NameCheckPanel = ({ nameCheck, t }) => {
    const { insuredNames, drivers, matchedRoles } = nameCheck;
    const isMatched = matchedRoles.length > 0;
    const hasAdditionalDriver = drivers.some((driver) => driver.role === "additionalDriver");

    const title = (() => {
        if (matchedRoles.length > 1) return t("resDetails.axleNameCheckMatchBoth");
        if (matchedRoles[0] === "renter") return t("resDetails.axleNameCheckMatchRenter");
        if (matchedRoles[0] === "additionalDriver") return t("resDetails.axleNameCheckMatchAdditionalDriver");
        return hasAdditionalDriver ? t("resDetails.axleNameCheckNoMatchEither") : t("resDetails.axleNameCheckNoMatch");
    })();

    return (
        <div
            className={`flex flex-col gap-1.5 rounded-lg border px-3 py-2.5 ${
                isMatched ? "border-[#ABEFC6] bg-[#ECFDF3]" : "border-[#FEDF89] bg-[#FFFAEB]"
            }`}>
            <p
                className={`m-0 flex items-center gap-1.5 text-xs font-semibold ${
                    isMatched ? "text-[#027A48]" : "text-[#B54708]"
                }`}>
                {isMatched ? <CheckCircleFilled /> : <WarningFilled />}
                {title}
            </p>
            <DetailRow label={t("resDetails.axlePolicyholder")}>{insuredNames.join(", ")}</DetailRow>
            {drivers.map((driver) => (
                <DetailRow
                    key={driver.role}
                    label={
                        driver.role === "renter" ? t("resDetails.axleRenter") : t("resDetails.axleAdditionalDriver")
                    }>
                    {driver.name}
                    {driver.matchedInsuredName ? (
                        <span className="ml-2 inline-flex items-center gap-1 text-[#027A48]">
                            <FaCheck size={9} />
                            {t("resDetails.axleMatchesName", { name: driver.matchedInsuredName })}
                        </span>
                    ) : (
                        <span className="ml-2 text-[#B54708]">{t("resDetails.axleNoMatch")}</span>
                    )}
                </DetailRow>
            ))}
        </div>
    );
};

NameCheckPanel.propTypes = {
    nameCheck: PropTypes.shape({
        insuredNames: PropTypes.arrayOf(PropTypes.string).isRequired,
        drivers: PropTypes.arrayOf(
            PropTypes.shape({
                role: PropTypes.oneOf(["renter", "additionalDriver"]).isRequired,
                name: PropTypes.string.isRequired,
                matchedInsuredName: PropTypes.string,
            }),
        ).isRequired,
        matchedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    t: PropTypes.func.isRequired,
};

const AxlePolicyDetails = ({ resDetails, policy, t }) => {
    const [openingDocumentId, setOpeningDocumentId] = useState(null);

    const isManual = isDocumentVerified(policy);

    // The policy must be in force for the whole trip: effective by pick-up, not expired before drop-off
    const pickupTime = resDetails?.exactPickupTime || resDetails?.pickupDate;
    const dropoffTime = resDetails?.exactDropoffTime || resDetails?.dropoffDate;
    const isInactive = policy?.isActive === false;
    const isNotYetEffective = Boolean(
        policy?.effectiveDate && pickupTime && new Date(policy.effectiveDate) > new Date(pickupTime),
    );
    const isExpiredBeforeDropoff = Boolean(
        policy?.expirationDate && new Date(policy.expirationDate) < new Date(dropoffTime || Date.now()),
    );
    const isPolicyValid = !isInactive && !isNotYetEffective && !isExpiredBeforeDropoff;

    const nameCheck = getAxleNameCheck(resDetails, policy);

    const vehicles = (policy?.properties || [])
        .filter((property) => property?.type === "vehicle" && property?.data)
        .map((property) => ({
            id: property.id,
            name: [property.data.year, property.data.make, property.data.model].filter(Boolean).join(" "),
            vin: property.data.vin,
        }));

    const coverages = isPolicyValid ? policy?.coverages || [] : [];
    const documents = (policy?.documents || []).filter((doc) => doc?.url);

    // Axle document URLs are S3 links signed for one hour, so re-fetch the policy for a fresh one
    const handleViewDocument = useCallback(
        async (document) => {
            if (openingDocumentId) return;

            // Open the tab synchronously so the browser doesn't block it as a popup after the await
            const newTab = window.open("", "_blank");
            if (newTab) newTab.opener = null;

            setOpeningDocumentId(document.id);
            let documentUrl = document.url;
            try {
                const { accessToken, policyId } = resDetails?.axleInsurance || {};
                if (accessToken && policyId) {
                    const { data } = await apiClient.post("/api/insurance/get-axle-insurance-policies", {
                        accessToken,
                        policyId,
                    });
                    const freshDocument = data?.data?.documents?.find((doc) => doc?.id === document.id);
                    if (freshDocument?.url) documentUrl = freshDocument.url;
                }
            } catch {
                // fall back to the URL from the initial load
            } finally {
                setOpeningDocumentId(null);
            }

            if (newTab) {
                newTab.location.href = documentUrl;
            } else {
                window.open(documentUrl, "_blank", "noopener,noreferrer");
            }
        },
        [openingDocumentId, resDetails?.axleInsurance],
    );

    const getDocumentLabel = (document) => {
        if (document.documentType === "id-card") return t("resDetails.viewInsuranceIdCard");
        return document.name || t("resDetails.axleViewDocument");
    };

    return (
        <div className="mt-2 flex flex-col gap-3">
            <span
                className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                    isManual ? "bg-[#FFFAEB] text-[#B54708]" : "bg-[#ECFDF3] text-[#027A48]"
                }`}>
                <span className={`h-2 w-2 rounded-full ${isManual ? "bg-[#F79009]" : "bg-[#12B76A]"}`} />
                {isManual ? t("resDetails.axleVerifiedByIdCard") : t("resDetails.axleVerifiedByAccount")}
            </span>

            {!isPolicyValid ? (
                <Alert
                    type="error"
                    showIcon
                    message={
                        isInactive
                            ? t("resDetails.policyInactive")
                            : isNotYetEffective
                              ? t("resDetails.axlePolicyNotYetEffective")
                              : t("resDetails.policyExpired")
                    }
                    description={
                        isNotYetEffective
                            ? t("resDetails.axlePolicyNotYetEffectiveDesc")
                            : isExpiredBeforeDropoff
                              ? t("resDetails.policyExpiredDesc")
                              : undefined
                    }
                />
            ) : null}

            <div className="flex flex-col gap-1.5">
                {policy?.carrier ? <DetailRow label={t("resDetails.carrier")}>{policy.carrier}</DetailRow> : null}
                {policy?.policyNumber ? (
                    <DetailRow label={t("resDetails.policyNumber")}>{policy.policyNumber}</DetailRow>
                ) : null}
                {policy?.effectiveDate || policy?.expirationDate ? (
                    <DetailRow label={t("resDetails.axlePolicyPeriod")}>
                        {formatPolicyDate(policy.effectiveDate) || "—"} –{" "}
                        {formatPolicyDate(policy.expirationDate) || "—"}
                    </DetailRow>
                ) : null}
                {vehicles.length > 0 ? (
                    <DetailRow label={t("resDetails.axleInsuredVehicle")}>
                        {vehicles.map((vehicle) => (
                            <span key={vehicle.id || vehicle.vin} className="block">
                                {vehicle.name}
                                {vehicle.vin ? <span className="ml-1 text-[#999]">(VIN {vehicle.vin})</span> : null}
                            </span>
                        ))}
                    </DetailRow>
                ) : null}
            </div>

            {nameCheck.insuredNames.length > 0 && nameCheck.drivers.length > 0 ? (
                <NameCheckPanel nameCheck={nameCheck} t={t} />
            ) : null}

            {coverages.length > 0 ? (
                <div className="space-y-1">
                    <p className="fontSemi" style={{ fontSize: "0.8rem", color: "#666", marginBottom: "0.5rem" }}>
                        {t("resDetails.coverages")}:
                    </p>
                    {coverages.map((coverage, index) => (
                        <div key={coverage.code || index} className="flex items-start gap-2">
                            <div className="mt-0.5 shrink-0">
                                <FaCheck color="green" size={10} />
                            </div>
                            <p className="m-0 flex-1 break-words text-xs leading-tight text-[#666]">
                                {formatCoverageText(coverage, t)}
                            </p>
                        </div>
                    ))}
                </div>
            ) : isPolicyValid ? (
                <p className="m-0 text-xs text-[#6B7280]">
                    {isManual ? t("resDetails.axleIdCardUploadedDesc") : t("resDetails.axleNoCoverageDetails")}
                </p>
            ) : null}

            {documents.length > 0 ? (
                <div className="flex flex-col items-start gap-1">
                    {documents.map((document) => (
                        <button
                            key={document.id}
                            type="button"
                            disabled={Boolean(openingDocumentId)}
                            onClick={() => handleViewDocument(document)}
                            className="text-sm font-medium text-[#5D5DFF] hover:text-[#4d4dff] underline bg-transparent border-0 p-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                            {getDocumentLabel(document)}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

AxlePolicyDetails.propTypes = {
    resDetails: PropTypes.object,
    policy: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

export default AxlePolicyDetails;
